import type { Filter } from './useStreamFilters'

export type ScopedContentSurface = 'streams' | 'memes' | 'ordinals' | 'private' | 'market'
export type ScopedContentTabKey =
    | 'discover'
    | 'items'
    | 'activity'
    | 'mine'
    | 'following'
    | 'all'
    | 'threads'
    | 'mentions'
    | 'messages'

export type ScopedContentTabDefinition = {
    key: ScopedContentTabKey
    label: string
    testId: string
}

const scopedTab = (
    surface: ScopedContentSurface,
    key: ScopedContentTabKey,
    label: string,
): ScopedContentTabDefinition => ({
    key,
    label,
    testId: `${surface}-tab-${key}`,
})

export function scopedContentTabs(surface: ScopedContentSurface): ScopedContentTabDefinition[] {
    // Two lenses on one market: Activity is the event feed (listing / offer /
    // sale cards) and the default; Items is the inventory (ordinals with an
    // open ask).
    if (surface === 'market') {
        return [scopedTab(surface, 'activity', 'Activity'), scopedTab(surface, 'items', 'Items')]
    }

    if (surface === 'private') {
        // Direct messages live in the sidebar (QuestHistory's DMs option).
        return [
            scopedTab(surface, 'all', 'All'),
            scopedTab(surface, 'threads', 'My Threads'),
            scopedTab(surface, 'mentions', 'Mentions'),
        ]
    }

    const personalLabel = {
        streams: 'My Channels',
        memes: 'My Memes',
        ordinals: 'My Ordinals',
    }[surface]

    const tabs = [scopedTab(surface, 'discover', 'Discover')]
    if (surface === 'streams') tabs.push(scopedTab(surface, 'following', 'Following'))
    tabs.push(scopedTab(surface, 'mine', personalLabel))

    return tabs
}

export function scopedContentSurface(filter?: Filter | null): ScopedContentSurface | null {
    // The Items lens carries `ordinals` too, so Market must win over Ordinals.
    if (filter?.market) return 'market'
    if (filter?.private) return 'private'
    if (filter?.streams) return 'streams'
    if (filter?.memes) return 'memes'
    if (filter?.ordinals) return 'ordinals'
    return null
}

export function activeScopedContentTab(filter?: Filter | null): ScopedContentTabKey {
    if (filter?.market) return filter?.market_cards ? 'activity' : 'items'
    // A direct-message feed (the sidebar's DMs, message requests) is no tab.
    if (filter?.private_messages || filter?.private_message_requests) return 'messages'
    if (filter?.private_mentions) return 'mentions'
    if (filter?.private_all) return 'all'
    if (
        filter?.streams_scope === 'mine' ||
        filter?.memes_scope === 'mine' ||
        filter?.ordinals_scope === 'mine'
    ) {
        return 'mine'
    }
    if (filter?.streams_scope === 'following') return 'following'
    if (filter?.private) return 'threads'
    return 'discover'
}

export function buildScopedContentTabFilter(
    surface: ScopedContentSurface,
    activeTab: ScopedContentTabKey,
): Filter {
    if (surface === 'market') {
        // Activity pins its own order so a saved Items order (price) cannot
        // leak into the card feed.
        return activeTab === 'activity'
            ? { market: true, market_cards: true, order: 'time' }
            : { market: true, ordinals: true }
    }

    if (surface === 'private') {
        if (activeTab === 'messages') {
            return { private: true, private_messages: true, order: 'time' }
        }
        if (activeTab === 'mentions') {
            return { private: true, private_mentions: true, order: 'time' }
        }
        if (activeTab === 'all') {
            return { private: true, private_all: true, order: 'time' }
        }
        return { private: true, order: 'time' }
    }

    if (surface === 'streams') {
        if (activeTab === 'mine') return { streams: true, streams_scope: 'mine' }
        if (activeTab === 'following') {
            return { streams: true, streams_scope: 'following' }
        }
        return { streams: true }
    }

    if (surface === 'memes') {
        return activeTab === 'mine' ? { memes: true, memes_scope: 'mine' } : { memes: true }
    }

    if (activeTab === 'mine') return { ordinals: true, ordinals_scope: 'mine' }
    return { ordinals: true }
}

// Surfaces whose tabs share one sort row: the chosen order (and Top window)
// follows the viewer from Discover to their own list and back.
const ORDER_CARRYING_SURFACES: ReadonlySet<ScopedContentSurface> = new Set(['memes', 'ordinals'])

export function scopedContentTabFilterKeepingOrder(
    surface: ScopedContentSurface,
    activeTab: ScopedContentTabKey,
    current?: Filter | null,
): Filter {
    const next = buildScopedContentTabFilter(surface, activeTab)
    if (!ORDER_CARRYING_SURFACES.has(surface) || next.order || !current?.order) return next
    return {
        ...next,
        order: current.order,
        ...(current.top_window ? { top_window: current.top_window } : {}),
    }
}
